import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import AddressConfirmation from '../../components/AddressConfirmation';
import SpecialServiceRequest from '../../components/SpecialServiceRequest';
import ExitIntentPopup from '../../components/ExitIntentPopup';
import LoyaltyStatus, { LoyaltyDiscount } from '../../components/LoyaltyStatus';
import { TrustBadges, useTrustStats, AnimatedCounter } from '../../components/TrustSignals';
import { SurgeStatusBadge, DynamicPriceDisplay } from '../../components/DynamicPricing';
import { CustomerTroubleshootingModal } from '../../components/TroubleshootingVideos';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Truck, Battery, Car, Key, Wrench, Shield, MapPin, ArrowLeft, 
  Loader2, Check, ChevronRight, Sparkles, X, Users, Star, CheckCircle, Zap, Flame, Tag, Clock, Video
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  { type: 'tow', icon: Truck, title: 'Towing', desc: 'Vehicle tow to your location', price: 150 },
  { type: 'jump_start', icon: Battery, title: 'Jump Start', desc: 'Battery jump start service', price: 50 },
  { type: 'flat_tire', icon: Car, title: 'Flat Tire', desc: 'Tire change assistance', price: 75 },
  { type: 'lockout', icon: Key, title: 'Lockout', desc: 'Vehicle lockout assistance', price: 60 },
  { type: 'repair', icon: Wrench, title: 'Mobile Repair', desc: 'On-site mechanical repair', price: 200 },
  { type: 'diagnostic', icon: Shield, title: 'Diagnostic', desc: 'Vehicle diagnostic check', price: 100 },
];

const steps = ['Service', 'Location', 'Vehicle', 'Confirm'];

// Compact trust stats for request page
function TrustStatsCompact() {
  const stats = useTrustStats();
  
  return (
    <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 border border-red-100">
      <div className="flex items-center justify-center gap-6 text-sm flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#FF3008]" />
          <span className="font-semibold text-gray-900">
            <AnimatedCounter end={stats.totalUsers} />+ Users
          </span>
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <span className="font-semibold text-gray-900">
            <AnimatedCounter end={stats.totalServicesCompleted} /> Services
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
          <span className="font-semibold text-gray-900">{stats.averageRating} Rating</span>
        </div>
      </div>
      <p className="text-center text-xs text-gray-500 mt-2">
        Trusted by customers across Fort Wayne
      </p>
    </div>
  );
}

export default function RequestService() {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type');
  const isSpecial = searchParams.get('special') === 'true';
  
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showSpecialRequest, setShowSpecialRequest] = useState(isSpecial);
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [offerCode, setOfferCode] = useState(null);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(null);
  
  const [formData, setFormData] = useState({
    service_type: initialType || '',
    description: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: new Date().getFullYear(),
    location: {
      lat: null,
      lng: null,
      address: ''
    }
  });
  
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [dynamicPricing, setDynamicPricing] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [showTroubleshootVideos, setShowTroubleshootVideos] = useState(false);
  const [pendingServiceType, setPendingServiceType] = useState(null);

  useEffect(() => {
    if (initialType) {
      setCurrentStep(1);
    }
  }, [initialType]);

  // Fetch dynamic pricing when service is selected
  useEffect(() => {
    const fetchDynamicPrice = async () => {
      if (!formData.service_type) return;
      
      setLoadingPrice(true);
      try {
        const response = await axios.get(`${API}/dynamic-pricing/calculate/${formData.service_type}?include_ai=true`);
        setDynamicPricing(response.data);
      } catch (error) {
        console.error('Failed to fetch dynamic price:', error);
        // Fall back to static pricing if dynamic pricing fails
        setDynamicPricing(null);
      } finally {
        setLoadingPrice(false);
      }
    };
    
    fetchDynamicPrice();
  }, [formData.service_type]);

  // Handle address confirmation from AddressConfirmation component
  const handleAddressConfirm = (confirmedData) => {
    setFormData(prev => ({
      ...prev,
      location: {
        lat: confirmedData.location.lat,
        lng: confirmedData.location.lng,
        address: confirmedData.address
      }
    }));
    setAddressConfirmed(true);
  };

  const getCurrentLocation = () => {
    setGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          // Reverse geocode to get address (using a simple placeholder)
          const address = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
          setFormData(prev => ({
            ...prev,
            location: { lat: latitude, lng: longitude, address }
          }));
          setGettingLocation(false);
          toast.success('Location detected!');
        },
        (error) => {
          console.error('Geolocation error:', error);
          toast.error('Could not get your location. Please enter manually.');
          setGettingLocation(false);
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
      setGettingLocation(false);
    }
  };

  const handleServiceSelect = (type) => {
    // Show troubleshooting videos first as an option
    setPendingServiceType(type);
    setShowTroubleshootVideos(true);
  };

  const proceedWithService = () => {
    setFormData(prev => ({ ...prev, service_type: pendingServiceType }));
    setShowTroubleshootVideos(false);
    setCurrentStep(1);
  };

  const skipTroubleshootVideos = () => {
    // User wants to try DIY - just close the modal
    setShowTroubleshootVideos(false);
    setPendingServiceType(null);
    toast.success('Good luck with your DIY fix! Come back if you need help.');
  };

  const handleSubmit = async () => {
    if (!formData.service_type || !formData.location.lat || !formData.vehicle_make) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Calculate final price considering dynamic pricing, loyalty discount, and exit intent offers
      const basePrice = dynamicPricing?.final_price || selectedService?.price || 0;
      const finalPrice = loyaltyDiscount?.discountedPrice || discountedPrice || basePrice;
      
      const requestData = {
        ...formData,
        estimated_price: finalPrice,
        dynamic_pricing_applied: !!dynamicPricing,
        pricing_breakdown: dynamicPricing?.breakdown || null
      };
      
      const response = await axios.post(
        `${API}/services`,
        requestData,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.success('Service request submitted!');
      navigate(`/customer/tracking/${response.data.request_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return formData.service_type;
      case 1: return formData.location.lat && formData.location.address;
      case 2: return formData.vehicle_make && formData.vehicle_model && formData.vehicle_year;
      case 3: return true;
      default: return false;
    }
  };

  const selectedService = services.find(s => s.type === formData.service_type);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="request-service-page">
      <Header />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button 
          onClick={() => {
            if (showSpecialRequest) {
              setShowSpecialRequest(false);
            } else if (currentStep > 0) {
              setCurrentStep(currentStep - 1);
            } else {
              navigate('/customer');
            }
          }}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </button>

        {/* Special Service Request Modal/View */}
        {showSpecialRequest ? (
          <SpecialServiceRequest 
            onClose={() => setShowSpecialRequest(false)}
            onSubmitSuccess={(data) => {
              navigate(`/customer/tracking/${data.request_id}`);
            }}
          />
        ) : (
          <>
            {/* Progress Steps */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                {steps.map((step, idx) => (
                  <div key={step} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                      idx < currentStep 
                        ? 'bg-green-500 text-white' 
                        : idx === currentStep 
                          ? 'bg-[#FF3008] text-white' 
                          : 'bg-gray-200 text-gray-500'
                    }`}>
                      {idx < currentStep ? <Check className="w-4 h-4" /> : idx + 1}
                    </div>
                    <span className={`ml-2 text-sm font-medium ${
                      idx === currentStep ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      {step}
                    </span>
                    {idx < steps.length - 1 && (
                      <div className={`w-12 md:w-24 h-1 mx-2 rounded ${
                        idx < currentStep ? 'bg-green-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Step Content */}
            <Card data-testid="step-content">
              <CardContent className="py-8">
                {/* Step 0: Select Service */}
                {currentStep === 0 && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2 font-['Chivo']">
                      What do you need help with?
                    </h2>
                    <p className="text-gray-600 mb-4">Select the service you need</p>
                    
                    {/* Surge Status Banner */}
                    <div className="mb-6 flex justify-center">
                      <SurgeStatusBadge />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {services.map((service) => (
                        <button
                          key={service.type}
                          onClick={() => handleServiceSelect(service.type)}
                          className={`p-4 border-2 rounded-lg text-left transition-all ${
                            formData.service_type === service.type
                              ? 'border-[#FF3008] bg-red-50 service-card-active'
                              : 'border-gray-200 hover:border-[#FF3008]/50'
                          }`}
                          data-testid={`service-option-${service.type}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                              formData.service_type === service.type ? 'bg-[#FF3008]' : 'bg-gray-100'
                            }`}>
                              <service.icon className={`w-6 h-6 ${
                                formData.service_type === service.type ? 'text-white' : 'text-gray-600'
                              }`} />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900">{service.title}</h3>
                              <p className="text-sm text-gray-500">{service.desc}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-[#FF3008] font-semibold">From ${service.price}</p>
                                <span className="text-xs text-gray-400 flex items-center gap-1">
                                  <Zap className="w-3 h-3" />
                                  AI Pricing
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Special Service Request Option */}
                    <div className="mt-6 pt-6 border-t">
                      <button
                        onClick={() => setShowSpecialRequest(true)}
                        className="w-full p-4 border-2 border-dashed border-purple-300 rounded-lg text-left transition-all hover:border-purple-500 hover:bg-purple-50 group"
                        data-testid="special-request-btn"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center group-hover:scale-105 transition-transform">
                            <Sparkles className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">Something else? Submit a Special Request</h3>
                            <p className="text-sm text-gray-500">Describe your unique situation and get an AI-powered price estimate</p>
                            <p className="text-purple-600 font-semibold mt-1 flex items-center gap-1">
                              <Sparkles className="w-4 h-4" />
                              AI-Powered Pricing
                            </p>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}

            {/* Step 1: Location */}
            {currentStep === 1 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2 font-['Chivo']">
                  Where are you?
                </h2>
                <p className="text-gray-600 mb-6">Confirm your exact location for quick assistance</p>
                
                <AddressConfirmation
                  initialAddress={formData.location.address}
                  initialLocation={formData.location.lat ? { lat: formData.location.lat, lng: formData.location.lng } : null}
                  onConfirm={handleAddressConfirm}
                />

                {addressConfirmed && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">Address confirmed!</span>
                    </div>
                    <p className="text-sm text-green-600 mt-1">{formData.location.address}</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Vehicle Info */}
            {currentStep === 2 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2 font-['Chivo']">
                  Vehicle Information
                </h2>
                <p className="text-gray-600 mb-6">Tell us about your vehicle</p>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="make">Make</Label>
                      <Input
                        id="make"
                        value={formData.vehicle_make}
                        onChange={(e) => setFormData(prev => ({ ...prev, vehicle_make: e.target.value }))}
                        placeholder="Toyota"
                        className="mt-1 h-12"
                        data-testid="vehicle-make"
                      />
                    </div>
                    <div>
                      <Label htmlFor="model">Model</Label>
                      <Input
                        id="model"
                        value={formData.vehicle_model}
                        onChange={(e) => setFormData(prev => ({ ...prev, vehicle_model: e.target.value }))}
                        placeholder="Camry"
                        className="mt-1 h-12"
                        data-testid="vehicle-model"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="year">Year</Label>
                    <Input
                      id="year"
                      type="number"
                      value={formData.vehicle_year}
                      onChange={(e) => setFormData(prev => ({ ...prev, vehicle_year: parseInt(e.target.value) }))}
                      min="1980"
                      max={new Date().getFullYear() + 1}
                      className="mt-1 h-12"
                      data-testid="vehicle-year"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Problem Description (Optional)</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe your issue in detail..."
                      className="mt-1"
                      rows={4}
                      data-testid="problem-description"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Confirm */}
            {currentStep === 3 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2 font-['Chivo']">
                  Confirm Your Request
                </h2>
                <p className="text-gray-600 mb-6">Review the details before submitting</p>
                
                <div className="space-y-4">
                  {/* Service Summary with Dynamic Pricing */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">SERVICE</h3>
                    {selectedService && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                          <selectedService.icon className="w-5 h-5 text-[#FF3008]" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{selectedService.title}</p>
                          {loadingPrice ? (
                            <div className="flex items-center gap-2 text-gray-500 text-sm">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              Calculating best price...
                            </div>
                          ) : dynamicPricing ? (
                            <div className="flex items-center gap-2">
                              <span className="text-[#FF3008] font-semibold">${dynamicPricing.final_price.toFixed(2)}</span>
                              {dynamicPricing.price_change_percent !== 0 && (
                                <span className={`text-xs flex items-center gap-1 ${
                                  dynamicPricing.is_surge ? 'text-red-600' : 'text-green-600'
                                }`}>
                                  {dynamicPricing.is_surge ? <Flame className="w-3 h-3" /> : <Tag className="w-3 h-3" />}
                                  {dynamicPricing.price_change_percent > 0 ? '+' : ''}{dynamicPricing.price_change_percent}%
                                </span>
                              )}
                            </div>
                          ) : (
                            <p className="text-[#FF3008] font-semibold">${selectedService.price}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Location Summary */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">LOCATION</h3>
                    <p className="text-gray-900">{formData.location.address}</p>
                  </div>

                  {/* Vehicle Summary */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">VEHICLE</h3>
                    <p className="text-gray-900">
                      {formData.vehicle_year} {formData.vehicle_make} {formData.vehicle_model}
                    </p>
                    {formData.description && (
                      <p className="text-gray-600 mt-1 text-sm">{formData.description}</p>
                    )}
                  </div>

                  {/* AI Dynamic Pricing Breakdown */}
                  {dynamicPricing && dynamicPricing.breakdown && (
                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Zap className="w-4 h-4 text-purple-600" />
                        <span className="font-medium text-purple-800">AI Price Optimization</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Base Price</span>
                          <span className="font-medium">${dynamicPricing.base_price.toFixed(2)}</span>
                        </div>
                        {dynamicPricing.breakdown.time_factors?.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {dynamicPricing.breakdown.time_factors.join(', ')}
                            </span>
                            <span className={dynamicPricing.breakdown.time_multiplier > 1 ? 'text-orange-600' : 'text-gray-600'}>
                              ×{dynamicPricing.breakdown.time_multiplier.toFixed(2)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600 capitalize">
                            {dynamicPricing.breakdown.demand_level} Demand
                          </span>
                          <span className={dynamicPricing.breakdown.demand_multiplier > 1 ? 'text-orange-600' : 
                            dynamicPricing.breakdown.demand_multiplier < 1 ? 'text-green-600' : 'text-gray-600'}>
                            ×{dynamicPricing.breakdown.demand_multiplier.toFixed(2)}
                          </span>
                        </div>
                        {dynamicPricing.breakdown.ai_adjustment !== 1 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              AI Optimization
                            </span>
                            <span className={dynamicPricing.breakdown.ai_adjustment > 1 ? 'text-orange-600' : 'text-green-600'}>
                              ×{dynamicPricing.breakdown.ai_adjustment.toFixed(2)}
                            </span>
                          </div>
                        )}
                        {dynamicPricing.breakdown.ai_reason && dynamicPricing.breakdown.ai_confidence > 0 && (
                          <p className="text-xs text-purple-600 italic pt-2 border-t border-purple-200">
                            {dynamicPricing.breakdown.ai_reason}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Estimated Price - Final Total */}
                  <div className={`p-4 border rounded-lg ${
                    dynamicPricing?.is_surge ? 'bg-red-50 border-red-200' :
                    dynamicPricing?.is_discount ? 'bg-green-50 border-green-200' :
                    'bg-orange-50 border-orange-200'
                  }`}>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Estimated Total</span>
                      <div className="text-right">
                        {loadingPrice ? (
                          <Loader2 className="w-5 h-5 animate-spin text-[#FF3008]" />
                        ) : (
                          <>
                            <span className="text-2xl font-bold text-gray-900 font-['Chivo']">
                              ${(dynamicPricing?.final_price || selectedService?.price || 0).toFixed(2)}
                            </span>
                            {dynamicPricing && dynamicPricing.base_price !== dynamicPricing.final_price && (
                              <p className="text-sm text-gray-500 line-through">
                                ${dynamicPricing.base_price.toFixed(2)}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-sm">
                      {dynamicPricing?.is_surge && (
                        <span className="text-red-600 flex items-center gap-1">
                          <Flame className="w-4 h-4" />
                          Surge pricing due to high demand
                        </span>
                      )}
                      {dynamicPricing?.is_discount && (
                        <span className="text-green-600 flex items-center gap-1">
                          <Tag className="w-4 h-4" />
                          You're getting a great rate!
                        </span>
                      )}
                      {!dynamicPricing?.is_surge && !dynamicPricing?.is_discount && (
                        <span className="text-gray-500">
                          Price valid for 15 minutes
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-4 mt-8">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="flex-1 h-12"
                  data-testid="prev-step-btn"
                >
                  Previous
                </Button>
              )}
              
              {currentStep < 3 ? (
                <Button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={!canProceed()}
                  className="flex-1 h-12 bg-[#FF3008] hover:bg-[#E02800]"
                  data-testid="next-step-btn"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 h-12 bg-[#FF3008] hover:bg-[#E02800]"
                  data-testid="submit-request-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Submit Request'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        </>
        )}

        {/* Loyalty Status - Show on step 3 (Confirm) */}
        {currentStep === 3 && !showSpecialRequest && (
          <div className="mt-4">
            <LoyaltyStatus />
            {selectedService && (
              <div className="mt-3">
                <LoyaltyDiscount 
                  originalPrice={discountedPrice || selectedService.price}
                  onDiscountCalculated={setLoyaltyDiscount}
                />
              </div>
            )}
          </div>
        )}

        {/* Trust Section - Always visible */}
        <div className="mt-8 space-y-4">
          <TrustBadges size="sm" />
          <TrustStatsCompact />
        </div>

        {/* Exit Intent Popup - Active when service is selected */}
        {formData.service_type && selectedService && (
          <ExitIntentPopup
            serviceType={formData.service_type}
            originalPrice={selectedService.price}
            onAccept={(data) => {
              setDiscountedPrice(data.newPrice);
              setOfferCode(data.offerCode);
              toast.success(`Discount applied! New price: $${data.newPrice}`);
            }}
            onDecline={() => {
              // User declined, continue normally
            }}
          />
        )}

        {/* Troubleshooting Videos Modal */}
        <CustomerTroubleshootingModal
          isOpen={showTroubleshootVideos}
          onClose={skipTroubleshootVideos}
          serviceType={pendingServiceType}
          serviceName={services.find(s => s.type === pendingServiceType)?.title}
          onProceedToOrder={proceedWithService}
        />
      </main>
    </div>
  );
}
